import { useMemo } from 'react';
import { useAppSelector } from '@/store';

/**
 * Checks if PMS integration is enabled for maintenance features.
 * Returns true when maintenance features should be available.
 * 
 * Works with both Yardi and Entrata PMS types.
 * 
 * @returns boolean - true if maintenance is enabled, false otherwise
 */
export const usePmsIntegrationEnabled = (): boolean => {
  const user = useAppSelector((s) => s.auth.user as any);

  return useMemo(() => {
    try {
      const pmsType = user?.property?.pmsType;
      
      // Maintenance is enabled for yardi or entrata PMS types
      if (pmsType === 'yardi' || pmsType === 'entrata') {
        // Check if integration is explicitly disabled via feature flag
        const flags = user?.property?.featureFlags?.flags || {};
        
        if (pmsType === 'entrata') {
          if (typeof flags['entrata-integration-disabled'] === 'boolean') {
            return !flags['entrata-integration-disabled'];
          }
          // Legacy fallback keys for backward compatibility
          if (typeof flags.entrataDisabled === 'boolean') return !flags.entrataDisabled;
          if (typeof flags.entrata_integration_disabled === 'boolean') return !flags.entrata_integration_disabled;
          // Fallback: if user indicates Entrata is inactive, treat as disabled
          if (typeof user?.entrataIsActive === 'boolean') {
            return user.entrataIsActive;
          }
        }
        
        if (pmsType === 'yardi') {
          if (typeof flags['yardi-integration-disabled'] === 'boolean') {
            return !flags['yardi-integration-disabled'];
          }
        }
        
        return true;
      }
      
      // Default: no recognized PMS type means disabled
      return false;
    } catch (error) {
      // If feature flag lookup fails, default to disabled
      return false;
    }
  }, [user]);
};

